import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { KeyRound } from 'lucide-react';
import { Modal, Button } from '@shared/components';
import { getUser } from '../../state/authSlice';
import { getCourierSetting } from '../../state/courierSlice';
import { usePasskeyManagement } from '../hooks/usePasskeyManagement';
import {
  shouldPromptPasskeyEnrolment,
  readSnooze,
  writeSnooze,
  clearSnooze,
} from '../../utils/passkeyPrompt';

interface PromptBodyProps {
  userId: number | string;
  isPasskeyEnabled?: boolean;
  snoozedUntil: Date | null;
}

/**
 * The half that talks to the server. Split out so the credential-list request
 * only happens for users who could actually act on the prompt — mounting this
 * unconditionally would add a request to every page load.
 */
const PromptBody = ({ userId, isPasskeyEnabled, snoozedUntil }: PromptBodyProps) => {
  const { credentials, isSupported, isLoading, isBusy, addPasskey } =
    usePasskeyManagement();
  const [dismissed, setDismissed] = useState(false);

  const shouldShow =
    !dismissed &&
    shouldPromptPasskeyEnrolment({
      isPasskeyEnabled,
      isSupported,
      credentialsLoaded: !isLoading,
      hasCredentials: credentials.length > 0,
      snoozedUntil,
      now: new Date(),
    });

  if (!shouldShow) return null;

  const handleSetUp = async () => {
    const { success } = await addPasskey();

    // A cancelled OS sheet comes back as {success:false} with no error. Leave
    // the prompt up rather than treating a mis-tap as a refusal.
    if (!success) return;

    clearSnooze(userId);
    setDismissed(true);
  };

  const handleNotNow = () => {
    writeSnooze(userId);
    setDismissed(true);
  };

  return (
    <Modal isOpen onClose={handleNotNow} title="Sign in faster next time" size="small">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 shrink-0 rounded-full bg-indigo-50 dark:bg-indigo-900/30 p-2">
            <KeyRound className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </span>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Set up a passkey and you can sign in with your fingerprint, face or
            screen lock instead of typing your password. It stays on this device.
          </p>
        </div>

        <Button
          variant="primary"
          fullWidth
          onClick={handleSetUp}
          disabled={isBusy}
          isLoading={isBusy}
        >
          Set up a passkey
        </Button>

        <button
          type="button"
          onClick={handleNotNow}
          className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Not now
        </button>
      </div>
    </Modal>
  );
};

/**
 * Offers a passkey to someone who has just signed in without one.
 *
 * The outer component answers everything it can locally — is the user signed
 * in, has the courier switched passkeys on, is there a live snooze — before
 * mounting the half that hits the API.
 *
 * There is no "did they use a password?" check: having no passkey already
 * means they did not sign in with one.
 */
const PasskeyEnrolmentPrompt = () => {
  const user = useSelector(getUser);
  const courierSetting = useSelector(getCourierSetting);

  if (!user?.id) return null;
  if (!courierSetting?.isPasskeyEnabled) return null;

  const snoozedUntil = readSnooze(user.id);
  if (snoozedUntil && snoozedUntil > new Date()) return null;

  return (
    <PromptBody
      userId={user.id}
      isPasskeyEnabled={courierSetting.isPasskeyEnabled}
      snoozedUntil={snoozedUntil}
    />
  );
};

export default PasskeyEnrolmentPrompt;
